/**
 * Manually derived from: https://shopify.dev/docs/api/admin-graphql/2023-04/objects/CompanyLocation
 * API Version: 2022-10
 */

import { FieldType } from "../../type-system/FieldType";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion, ShopifyApiVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";
import {
  ShopifyCompanyAddressKey,
  ShopifyCompanyBillingAddressToCompanyLocationKey,
  ShopifyCompanyContactRoleAssignmentKey,
  ShopifyCompanyContactRoleAssignmentToCompanyLocationKey,
  ShopifyCompanyKey,
  ShopifyCompanyLocationKey,
  ShopifyCompanyLocationToCompanyKey,
  ShopifyCompanyLocationToMarketKey,
  ShopifyCompanyLocationToShopKey,
  ShopifyCompanyShippingAddressToCompanyLocationKey,
  ShopifyDraftOrderKey,
  ShopifyDraftOrderToCompanyLocationKey,
  ShopifyMarketKey,
  ShopifyOrderKey,
  ShopifyOrderToCompanyLocationKey,
  ShopifyShopKey,
} from "./keys";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCompanyLocationKey,
  "2022-10": {
    name: "Shopify Company Location",
    fields: [
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt", shopifyFieldKey: "created_at" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "updatedAt", shopifyFieldKey: "updated_at" } },
      },
      {
        name: "Billing Address",
        fieldType: FieldType.HasOne,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingAddress",
            graphQLSelection: `
            id
            companyName
            address1
            address2
            city
            province
            zip
            zoneCode
            country
            countryCode
            phone
            formattedAddress
            formattedArea
            recipient
            `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyAddressKey,
          inverseFieldKey: ShopifyCompanyBillingAddressToCompanyLocationKey,
        },
      },
      {
        name: "Buyer Experience Configuration",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "buyerExperienceConfiguration",
            graphQLSelection: `
            checkoutToDraft
            editableShippingAddress
            payNowOnly
            paymentTermsTemplate {
              description
              dueInDays
              id
              name
              paymentTermsType
              translatedName
            }`,
          },
        },
      },
      {
        name: "Company",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyLocationToCompanyKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "company", graphQLSelection: "id" } },
        configuration: {
          relatedModelKey: ShopifyCompanyKey,
        },
      },
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "currency" } },
      },
      {
        name: "External ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "externalId" } },
      },
      {
        name: "Timeline Comment",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "hasTimelineComment" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Locale",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "locale", shopifyFieldKey: "locale" } },
      },
      {
        name: "Market",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyLocationToMarketKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "market", graphQLSelection: "id", shopifyFieldKey: "market" },
        },
        configuration: {
          relatedModelKey: ShopifyMarketKey,
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "name", shopifyFieldKey: "name" } },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "note", shopifyFieldKey: "note" } },
      },
      {
        name: "Order Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "orderCount" } },
        configuration: { decimals: 0 },
      },
      {
        name: "Phone",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "phone", shopifyFieldKey: "phone" } },
      },
      {
        name: "Shipping Address",
        fieldType: FieldType.HasOne,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "shippingAddress",
            graphQLSelection: `
            id
            companyName
            address1
            address2
            city
            province
            zip
            zoneCode
            country
            countryCode
            phone
            formattedAddress
            formattedArea
            recipient
            `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyAddressKey,
          inverseFieldKey: ShopifyCompanyShippingAddressToCompanyLocationKey,
        },
      },
      {
        name: "Tax Exemptions",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "taxExemptions" } },
        configuration: {
          allowOther: true,
          options: [{ name: "CA_BC_CONTRACTOR_EXEMPTION" }, { name: "CA_BC_RESELLER_EXEMPTION" }],
        },
      },
      {
        name: "Tax Registration ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "taxRegistrationId" } },
      },
      {
        name: "Total Spent",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "totalSpent",
            graphQLSelection: `
            amount
            currencyCode`,
          },
        },
      },
      // {
      //   name: "Catalogs", // we dont have a catalog model
      //   fieldType: FieldType.HasMany,
      //   configuration: {
      //     relatedModelKey: ShopifyCatalogKey,
      //     inverseFieldKey: ShopifyDraftOrderToCompanyLocationKey,
      //   },
      // },
      {
        name: "Draft Orders",
        fieldType: FieldType.HasMany,
        configuration: {
          relatedModelKey: ShopifyDraftOrderKey,
          inverseFieldKey: ShopifyDraftOrderToCompanyLocationKey,
        },
      },
      // {
      //   name: "Events",
      //   fieldType: FieldType.HasMany,
      //   configuration: {
      //     dependent: "delete",
      //     relatedModelKey: ShopifyEventKey,
      //     inverseFieldKey: ShopifyEventToCompanyLocationKey,
      //   },
      // },
      {
        name: "Orders",
        fieldType: FieldType.HasMany,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
          inverseFieldKey: ShopifyOrderToCompanyLocationKey,
        },
      },
      {
        name: "Role Assignments",
        fieldType: FieldType.HasMany,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCompanyContactRoleAssignmentKey,
          inverseFieldKey: ShopifyCompanyContactRoleAssignmentToCompanyLocationKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyLocationToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "companyLocations",
      filterByUpdatedAt: true,
      sortKey: "UPDATED_AT",
    },
  },
  "2023-01": {
    hasMetafields: true,
  },
  // "2023-04": {
  //   fields: [
  //     {
  //       name: "In Catalog", //needs an input of a CatalogID
  //       fieldType: FieldType.Boolean,
  //       contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "inCatalog" } },
  //     },
  //   ],
  // },
  "2024-04": {
    fields: [
      {
        name: "Order Count",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "orderCount" } },
        isDeleted: true,
      },
      {
        name: "Orders Count",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "ordersCount",
            graphQLSelection: `
        count
        precision`,
          },
        },
      },
    ],
  },
};

export const ShopifyCompanyLocation = ({ apiVersion }: { apiVersion: ShopifyApiVersion }) => buildConnectionSpec(specs, apiVersion);
